import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { AccountsService } from "../api";
import { checkPostCodeFormat, calPlanLimits } from "../libs";
import { validateUsernamePassword } from "../utils/validators";

import { toastActions } from "./toast";

interface EditingAccountState {
  editingPage: "create" | "update";
  id: number | undefined;
  email: string;
  userName: string;
  password: string;
  showPassword: boolean;
  planType: string;
  creditLimit: string;
  corporateName: string;
  storeName: string;
  picName: string;
  postCode: string;
  address: string;
  contractStart: string;
  contractEnd: string;
  prEmailAvail: string;
  registrationRoute: string;
  marketingChannel: string;
  remarks: string;
  realEstateType: string;
  showHashPassword: boolean;
  errors: Record<string, string>;
}

interface RootState {
  editingAccount: EditingAccountState;
}

interface ThunkAPI {
  getState: () => RootState;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  dispatch: (value: unknown) => unknown;
}

interface NavigateFunction {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  (to: string): void;
}

interface ApiAccount {
  id: number;
  representative_email?: string;
  representative_user_name?: string;
  real_estate_type?: string;
  plan_type_id?: string;
  credit_limit?: string;
  corporate_name?: string;
  store?: { name?: string };
  person_in_charge_name?: string;
  post_code?: string;
  address?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  promotional_email_avail?: string;
  registration_route?: string;
  marketing_channel?: string;
  remarks?: string;
}

const getEditAccount = createAsyncThunk(
  "getEditAccount",
  async (
    props: { id: number; navigate: NavigateFunction },
    thunkAPI: ThunkAPI,
  ) => {
    const { id, navigate } = props;
    const res = await AccountsService.accountsRetrieve({ id });
    if (!res.id) {
      thunkAPI.dispatch(
        toastActions.flashError(`アカウントが登録されていません。`),
      );
      navigate("/");
      return;
    }

    const editingAccount = apiAccountToEditingAccount(res);
    thunkAPI.dispatch(editingAccountActions.setEditingAccount(editingAccount));
  },
);

function apiAccountToEditingAccount(account: ApiAccount) {
  return {
    id: account.id,
    email: account.representative_email || "",
    userName: account.representative_user_name || "",
    realEstateType: account.real_estate_type || "",
    planType: account.plan_type_id || "",
    creditLimit: account.credit_limit || "",
    corporateName: account.corporate_name || "",
    storeName: account.store?.name || "",
    picName: account.person_in_charge_name || "",
    postCode: account.post_code || "",
    address: account.address || "",
    contractStart: account.contract_start_date || "",
    contractEnd: account.contract_end_date || "",
    prEmailAvail: account.promotional_email_avail || "",
    registrationRoute: account.registration_route || "",
    marketingChannel: account.marketing_channel || "",
    remarks: account.remarks || "",
  };
}

const submitForm = createAsyncThunk(
  "submitForm",
  async (navigate: NavigateFunction, thunkAPI: ThunkAPI) => {
    const {
      editingPage,
      email,
      userName,
      password,
      realEstateType,
      planType,
      creditLimit,
      corporateName,
      storeName,
      postCode,
      contractStart,
      prEmailAvail,
    } = thunkAPI.getState().editingAccount;

    thunkAPI.dispatch(editingAccountActions.setInitError());

    const errors: Record<string, string> = {};
    if (!corporateName) errors.corporateName = "法人名を入力してください。";
    if (!storeName) errors.storeName = "店舗名を入力してください。";
    if (!checkPostCodeFormat(postCode))
      errors.postCode = "郵便番号が無効です。";
    if (!email) errors.email = "メールアドレスを入力してください。";
    if (email && !isValidEmail(email))
      errors.email = "メールアドレスが無効です。";
    const userNameValidate = validateUsernamePassword(
      userName,
      "代表ユーザーID",
    );
    if (userNameValidate) errors.userName = userNameValidate;
    // アカウント作成時のみバリデーション
    if (editingPage === "create") {
      const passwordValidate = validateUsernamePassword(password, "パスワード");
      if (passwordValidate) errors.password = passwordValidate;
    }
    if (!realEstateType)
      errors.realEstateType = "不動産種別を入力してください。";
    if (!planType) errors.planType = "利用プランを入力してください。";
    if (!creditLimit) errors.creditLimit = "上限回数を入力してください。";
    if (!contractStart) errors.contractStart = "契約開始日を入力してください。";
    if (!prEmailAvail) errors.prEmailAvail = "販促メールを入力してください。";
    if (Object.keys(errors).length !== 0) {
      thunkAPI.dispatch(editingAccountActions.setErrors(errors));
      return;
    }
    if (editingPage === "create") {
      thunkAPI.dispatch(editingAccountActions.postCreateForm(navigate));
      return;
    }
    if (editingPage === "update") {
      thunkAPI.dispatch(editingAccountActions.postUpdateForm(navigate));
    }
  },
);

const postCreateForm = createAsyncThunk(
  "postCreateForm",
  async (navigate: NavigateFunction, thunkAPI: ThunkAPI) => {
    const APIParams = editingAccountToAPIParams(
      thunkAPI.getState().editingAccount,
    );
    const res = await AccountsService.accountsCreate({
      requestBody: APIParams,
    });
    if (res.id) {
      thunkAPI.dispatch(toastActions.flashSuccess(`情報が登録されました。`));
      navigate("/");
      return;
    }

    const { msg } = res as { msg?: string };
    if (msg === "existing_user_name") {
      thunkAPI.dispatch(
        editingAccountActions.setError({
          key: "userName",
          value: "ユーザーIDは既に存在しています。",
        }),
      );
      return;
    }

    thunkAPI.dispatch(toastActions.toastError(msg));
  },
);

const postUpdateForm = createAsyncThunk(
  "postUpdateForm",
  async (navigate: NavigateFunction, thunkAPI: ThunkAPI) => {
    const { id } = thunkAPI.getState().editingAccount;
    const APIParams = editingAccountToAPIParams(
      thunkAPI.getState().editingAccount,
    );
    const request = {
      id: id!,
      requestBody: APIParams,
    };
    console.log("アカウント更新API リクエスト:", request);

    const response = await AccountsService.accountsUpdate(request);
    console.log("アカウント更新API レスポンス:", response);

    if (response.id) {
      thunkAPI.dispatch(toastActions.flashSuccess(`情報が更新されました。`));
      navigate("/");
      return;
    }

    const { msg } = response as { msg?: string };
    if (msg === "existing_user_name") {
      thunkAPI.dispatch(
        editingAccountActions.setError({
          key: "userName",
          value: "ユーザーIDは既に存在しています。",
        }),
      );
      return;
    }

    thunkAPI.dispatch(toastActions.toastError(msg));
  },
);

function editingAccountToAPIParams(editingAccount: EditingAccountState) {
  return {
    id: 0,
    email: editingAccount.email,
    user_name: editingAccount.userName,
    password: editingAccount.password,
    real_estate_type: editingAccount.realEstateType,
    plan_type_id: editingAccount.planType,
    credit_limit: editingAccount.creditLimit,
    corporate_name: editingAccount.corporateName,
    store_name: editingAccount.storeName,
    person_in_charge_name: editingAccount.picName,
    post_code: editingAccount.postCode,
    address: editingAccount.address,
    contract_start_date: editingAccount.contractStart,
    contract_end_date: editingAccount.contractEnd,
    promotional_email_avail: editingAccount.prEmailAvail,
    registration_route: editingAccount.registrationRoute,
    marketing_channel: editingAccount.marketingChannel,
    remarks: editingAccount.remarks,
    created: "not used",
    modified: "not used",
  };
}

const calcCreditLimit = createAsyncThunk(
  "calcCreditLimit",
  async (_: void, thunkAPI: ThunkAPI) => {
    const { planType, realEstateType } = thunkAPI.getState().editingAccount;
    const creditLimit = calPlanLimits(realEstateType, planType);
    thunkAPI.dispatch(editingAccountActions.setCreditLimit(creditLimit));
  },
);

function isValidEmail(email: string) {
  const reg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return reg.test(email);
}

const initialError = {
  errors: {} as Record<string, string>,
};

const initialState: EditingAccountState = {
  editingPage: "create" as "create" | "update",
  id: undefined,
  email: "",
  userName: "",
  password: "",
  showPassword: false,
  planType: "",
  creditLimit: "",
  corporateName: "",
  storeName: "",
  picName: "",
  postCode: "",
  address: "",
  contractStart: "",
  contractEnd: "",
  prEmailAvail: "",
  registrationRoute: "",
  marketingChannel: "",
  remarks: "",
  realEstateType: "",
  showHashPassword: true,
  ...initialError,
};

const editingAccountSlice = createSlice({
  name: "editingAccount",
  initialState,
  reducers: {
    setId: (state, action: PayloadAction<number>) => {
      state.id = action.payload;
    },
    setEditingPage: (state, action: PayloadAction<"create" | "update">) => {
      state.editingPage = action.payload;
    },
    setEditingAccount: (
      state,
      action: PayloadAction<Partial<EditingAccountState>>,
    ) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setUserName: (state, action: PayloadAction<string>) => {
      state.userName = action.payload;
    },
    setPassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
    },
    switchShowPassword: (state) => {
      state.showPassword = !state.showPassword;
    },
    setPlanType: (state, action: PayloadAction<string>) => {
      state.planType = action.payload;
    },
    setCreditLimit: (state, action: PayloadAction<string>) => {
      state.creditLimit = action.payload.replace(/\D/g, "");
    },
    setCorporateName: (state, action: PayloadAction<string>) => {
      state.corporateName = action.payload;
    },
    setStoreName: (state, action: PayloadAction<string>) => {
      state.storeName = action.payload;
    },
    setPICName: (state, action: PayloadAction<string>) => {
      state.picName = action.payload;
    },
    setPostCode: (state, action: PayloadAction<string>) => {
      state.postCode = action.payload;
    },
    setAddress: (state, action: PayloadAction<string>) => {
      state.address = action.payload;
    },
    setContractStart: (state, action: PayloadAction<string>) => {
      state.contractStart = action.payload;
    },
    setContractEnd: (state, action: PayloadAction<string>) => {
      state.contractEnd = action.payload;
    },
    setPrEmailAvail: (state, action: PayloadAction<string>) => {
      state.prEmailAvail = action.payload;
    },
    setRegistrationRoute: (state, action: PayloadAction<string>) => {
      state.registrationRoute = action.payload;
    },
    setMarketingChannel: (state, action: PayloadAction<string>) => {
      state.marketingChannel = action.payload;
    },
    setRemarks: (state, action: PayloadAction<string>) => {
      state.remarks = action.payload;
    },
    setRealEstateType: (state, action: PayloadAction<string>) => {
      state.realEstateType = action.payload;
    },
    setShowHashPassword: (state, action: PayloadAction<boolean>) => {
      state.showHashPassword = action.payload;
    },
    setErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors = action.payload;
    },
    setError: (
      state,
      action: PayloadAction<{ key: string; value: string }>,
    ) => {
      const { key, value } = action.payload;
      state.errors[key] = value;
    },
    setInitState: () => {
      return initialState;
    },
    setInitError: (state) => {
      return {
        ...state,
        ...initialError,
      };
    },
  },
});

const editingAccountActions = {
  ...editingAccountSlice.actions,
  calcCreditLimit,
  getEditAccount,
  submitForm,
  postCreateForm,
  postUpdateForm,
};
export { editingAccountActions };
export default editingAccountSlice.reducer;
